import { useEffect, useMemo, useState } from "react";
import { Activity, Database, Home, Info, Layers3, ShieldCheck } from "lucide-react";
import DataStatusStrip from "./components/DataStatusStrip";
import DiagnosticExportButton from "./components/DiagnosticExportButton";
import DetailPanel from "./components/DetailPanel";
import LagAnalysisTable from "./components/LagAnalysisTable";
import HousingTransmissionSection from "./components/HousingTransmissionSection";
import MetricCard from "./components/MetricCard";
import SectionHeader from "./components/SectionHeader";
import SituationPanel from "./components/SituationPanel";
import TabNarrative from "./components/TabNarrative";
import TimeSeriesChart from "./components/TimeSeriesChart";
import { FED_SERIES } from "./data/adapters/fedPolicy";
import { TREASURY_SERIES } from "./data/adapters/usTreasury";
import { ECOS_SERIES } from "./data/ecosCatalog";
import { unavailableSeries } from "./data/derived";
import { buildSituationItems, metricHelp, metricTakeaway } from "./data/interpretation";
import { buildSituationAnalysis } from "./data/situationAnalysis";
import { buildTabNarratives } from "./data/tabNarratives";
import { buildHousingPolicyBundle } from "./data/housingPolicyRegimes";
import { createBlockedHousingRelationshipAnalysis } from "./data/housingRelationshipAnalysis";
import { buildLegacyMortgageStress } from "./data/legacyMortgageStress";
import { isUsableSeries } from "./data/freshness.ts";
import { sourceRegistry, sourceStatusLabels } from "./data/sourceRegistry";
import { useCoreData } from "./hooks/useCoreData";
import type { IndicatorSeries, RangeKey } from "./types";
import { latestChange, latestValue } from "./utils/series";

const ranges: RangeKey[] = ["1Y", "3Y", "5Y", "10Y", "MAX"];
const APP_VERSION = "0.7.0-v18.7";
const TAB_KEYS = ["overview", "rates", "global", "credit", "housing", "real", "sources"] as const;
type TabKey = typeof TAB_KEYS[number];

function tabFromHash(): TabKey {
  const hash = window.location.hash.replace("#", "");
  return (TAB_KEYS as readonly string[]).includes(hash) ? hash as TabKey : "overview";
}

type DetailSpec = {
  title: string;
  description: string;
  series: IndicatorSeries[];
  referenceZero?: boolean;
} | null;

function fmt(v: number | null, digits = 2) {
  return v == null ? "N/A" : v.toFixed(digits);
}

function metricStatus(series: IndicatorSeries, v: number | null) {
  if (v == null) return "na" as const;
  return series.source.quality === "stale" ? "stale" as const : "data" as const;
}

export default function App() {
  const [range, setRange] = useState<RangeKey>("3Y");
  const [detail, setDetail] = useState<DetailSpec>(null);
  const [activeSection, setActiveSection] = useState<TabKey>(() => tabFromHash());
  const { data, status } = useCoreData();

  const s = (id: string, fallbackName: string, shortName: string, layer: IndicatorSeries["layer"], frequency: IndicatorSeries["source"]["frequency"] = "monthly") =>
    data?.series[id] ?? unavailableSeries(id, fallbackName, shortName, layer, frequency);

  const bok = s(ECOS_SERIES.bokBaseRate.id, "한국은행 기준금리", "BOK", "policy", "daily");
  const ktb3 = s(ECOS_SERIES.ktb3.id, "국고채 3년", "KTB 3Y", "market", "daily");
  const ktb5 = s(ECOS_SERIES.ktb5.id, "국고채 5년", "KTB 5Y", "market", "daily");
  const ktb10 = s(ECOS_SERIES.ktb10.id, "국고채 10년", "KTB 10Y", "market", "daily");
  const cd91 = s(ECOS_SERIES.cd91.id, "CD 91일", "CD 91D", "market", "daily");
  const koribor12 = s(ECOS_SERIES.koribor12.id, "KORIBOR 12개월", "KORIBOR 12M", "market", "daily");
  const usdKrw = s(ECOS_SERIES.usdKrw.id, "원/달러 환율 매매기준율", "USD/KRW", "market", "daily");
  const ust2 = s(TREASURY_SERIES.ust2.id, "미국 국채 2년", "UST 2Y", "market", "daily");
  const ust3 = s(TREASURY_SERIES.ust3.id, "미국 국채 3년", "UST 3Y", "market", "daily");
  const ust5 = s(TREASURY_SERIES.ust5.id, "미국 국채 5년", "UST 5Y", "market", "daily");
  const ust10 = s(TREASURY_SERIES.ust10.id, "미국 국채 10년", "UST 10Y", "market", "daily");
  const fedLower = s(FED_SERIES.lower.id, "Fed 목표금리 하단", "Fed lower", "policy", "daily");
  const fedUpper = s(FED_SERIES.upper.id, "Fed 목표금리 상단", "Fed upper", "policy", "daily");
  const fedMid = s(FED_SERIES.midpoint.id, "Fed 목표금리 중간값", "Fed mid", "policy", "daily");
  const fundingNew = s(ECOS_SERIES.fundingNew.id, "예금은행 금융채 신규취급금리", "Funding new", "funding");
  const fundingStock = s(ECOS_SERIES.fundingOutstanding.id, "예금은행 금융채 잔액금리", "Funding stock", "funding");
  const corpLoan = s(ECOS_SERIES.corpLoan.id, "기업 신규대출금리", "Corp loan", "credit");
  const householdLoan = s(ECOS_SERIES.householdLoan.id, "가계 신규대출금리", "Household loan", "credit");
  const mortgageLoan = s(ECOS_SERIES.mortgageLoan.id, "주담대 신규취급금리", "Mortgage", "credit");
  const economicSentiment = s(ECOS_SERIES.economicSentiment.id, "경제심리지수", "ESI", "real_economy");
  const industrialProduction = s(ECOS_SERIES.industrialProduction.id, "전산업생산지수(농림어업제외)", "All-industry production", "real_economy");
  const unsoldTotal = s(ECOS_SERIES.unsoldTotal.id, "전국 미분양주택", "Unsold homes", "real_estate");
  const corpAAFair = data?.series[ECOS_SERIES.corpAA3Fair.id];
  const corpAARegular = data?.series[ECOS_SERIES.corpAA3.id];
  const corpAA = corpAAFair?.source.quality === "official"
    ? corpAAFair
    : corpAARegular?.source.quality === "official"
      ? corpAARegular
      : isUsableSeries(corpAAFair)
        ? corpAAFair!
        : s(ECOS_SERIES.corpAA3.id, "회사채 3년 AA-", "Corp AA- 3Y", "credit", "daily");

  const policyGap = data?.policyGap ?? unavailableSeries("policy_gap_3y", "국고채 3년 - 기준금리", "Policy gap", "market", "daily", "bp");
  const curve103 = data?.curve103 ?? unavailableSeries("curve_10y_3y", "국고채 10년 - 3년", "10Y-3Y", "market", "daily", "bp");
  const creditSpread = data?.creditSpread ?? unavailableSeries("credit_spread_aa", "회사채 AA- - 국고채 3년", "Credit spread", "credit", "daily", "bp");
  const fundingMomentum = data?.fundingMomentum ?? unavailableSeries("funding_new_minus_stock", "금융채 신규 - 잔액", "New-stock funding", "funding", "monthly", "bp");
  const corpLoanFundingSpread = data?.corpLoanFundingSpread ?? unavailableSeries("corp_loan_minus_funding", "기업대출 - 신규조달", "Corp loan-funding", "credit", "monthly", "bp");
  const mortgageFundingSpread = data?.mortgageFundingSpread ?? unavailableSeries("mortgage_minus_funding", "주담대 - 신규조달", "Mortgage-funding", "credit", "monthly", "bp");
  const krUs2Spread = data?.krUs2Spread ?? unavailableSeries("kr_us_2y_spread", "한국 국고채 2년 - 미국 국채 2년", "KR-US 2Y", "market", "daily", "bp");
  const krUs3Spread = data?.krUs3Spread ?? unavailableSeries("kr_us_3y_spread", "한국 국고채 3년 - 미국 국채 3년", "KR-US 3Y", "market", "daily", "bp");
  const bokFedSpread = data?.bokFedSpread ?? unavailableSeries("bok_fed_mid_spread", "한국은행 기준금리 - Fed 목표범위 중간값", "BOK-Fed mid", "policy", "daily", "bp");
  const events = data?.events ?? [];
  const housing = data?.housing ?? buildHousingPolicyBundle();
  const legacyMortgage = data?.legacyMortgage ?? buildLegacyMortgageStress({ newMortgageRate: mortgageLoan });

  useEffect(() => {
    const onHash = () => setActiveSection(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [activeSection]);

  const headline = useMemo(
    () => ({
      bok: latestValue(bok),
      ktb3: latestValue(ktb3),
      gap: latestValue(policyGap),
      curve: latestValue(curve103),
      funding: latestValue(fundingMomentum),
      credit: latestValue(creditSpread),
      existingMortgage: latestValue(legacyMortgage.inputs.existingMortgageCost),
      lockIn: latestValue(legacyMortgage.lockInSpread),
      housingOutlook: latestValue(legacyMortgage.inputs.housingPriceOutlook)
    }),
    [bok, ktb3, policyGap, curve103, fundingMomentum, creditSpread, legacyMortgage]
  );

  const situationItems = useMemo(() => data?.overview?.situationItems ?? buildSituationItems({
    policyGap,
    curve103,
    fundingMomentum,
    creditSpread,
    mortgageFundingSpread,
    corpLoanFundingSpread,
    economicSentiment,
    industrialProduction,
    unsoldTotal,
    housing,
    legacyMortgage
  }), [data?.overview, policyGap, curve103, fundingMomentum, creditSpread, mortgageFundingSpread, corpLoanFundingSpread, economicSentiment, industrialProduction, unsoldTotal, housing, legacyMortgage]);

  const situationAnalysis = useMemo(() => data?.overview?.situationAnalysis ?? buildSituationAnalysis({
    policyGap,
    fundingMomentum,
    creditSpread,
    economicSentiment,
    industrialProduction,
    unsoldTotal,
    housing,
    legacyMortgage
  }), [data?.overview, policyGap, fundingMomentum, creditSpread, economicSentiment, industrialProduction, unsoldTotal, housing, legacyMortgage]);

  const tabNarratives = useMemo(() => data?.overview?.tabNarratives ?? buildTabNarratives({
    bok,
    ktb3,
    curve103,
    policyGap,
    usdKrw,
    krUs2Spread,
    krUs3Spread,
    bokFedSpread,
    fundingNew,
    fundingStock,
    fundingMomentum,
    mortgageLoan,
    corpLoan,
    mortgageFundingSpread,
    corpLoanFundingSpread,
    creditSpread,
    economicSentiment,
    industrialProduction,
    unsoldTotal,
    housing,
    legacyMortgage,
    officialHousingPrices: data?.officialHousingPrices ?? [],
    status
  }), [data?.overview, bok, ktb3, curve103, policyGap, usdKrw, krUs2Spread, krUs3Spread, bokFedSpread, fundingNew, fundingStock, fundingMomentum, mortgageLoan, corpLoan, mortgageFundingSpread, corpLoanFundingSpread, creditSpread, economicSentiment, industrialProduction, unsoldTotal, housing, legacyMortgage, data?.officialHousingPrices, status]);

  const openDetail = (spec: NonNullable<DetailSpec>) => setDetail(spec);

  return (
    <div className="app-shell">
      <a className="skip-link" href="#main-content">본문으로 바로가기</a>
      <aside className="sidebar">
        <div className="brand">
          <div className="brand__mark"><Activity size={20} /></div>
          <div><strong>Shock Monitor</strong><span>금융→실물경제 전이</span></div>
        </div>
        <nav className="nav" aria-label="주요 메뉴">
          <a href="#overview" aria-current={activeSection === "overview" ? "page" : undefined} className={`nav__item ${activeSection === "overview" ? "nav__item--active" : ""}`}><Activity size={17}/>개요</a>
          <a href="#rates" aria-current={activeSection === "rates" ? "page" : undefined} className={`nav__item ${activeSection === "rates" ? "nav__item--active" : ""}`}><Layers3 size={17}/>금리·스프레드</a>
          <a href="#global" aria-current={activeSection === "global" ? "page" : undefined} className={`nav__item ${activeSection === "global" ? "nav__item--active" : ""}`}><Layers3 size={17}/>한미·환율</a>
          <a href="#credit" aria-current={activeSection === "credit" ? "page" : undefined} className={`nav__item ${activeSection === "credit" ? "nav__item--active" : ""}`}><ShieldCheck size={17}/>조달·대출 전이</a>
          <a href="#housing" aria-current={activeSection === "housing" ? "page" : undefined} className={`nav__item ${activeSection === "housing" ? "nav__item--active" : ""}`}><Home size={17}/>부동산 금리전이</a>
          <a href="#real" aria-current={activeSection === "real" ? "page" : undefined} className={`nav__item ${activeSection === "real" ? "nav__item--active" : ""}`}><Database size={17}/>실물경제</a>
          <a href="#sources" aria-current={activeSection === "sources" ? "page" : undefined} className={`nav__item ${activeSection === "sources" ? "nav__item--active" : ""}`}><Info size={17}/>데이터 상태</a>
        </nav>
        <div className="sidebar__notice">
          <Info size={16}/>
          <div>
            <strong>{APP_VERSION} · 공식 데이터 우선</strong>
            <span>누락·검증 실패는 N/A로 남기고 임의 보간·위기확률을 만들지 않습니다.</span>
          </div>
        </div>
      </aside>

      <main id="main-content" className="main" tabIndex={-1}>
        <header className="topbar">
          <div>
            <p className="kicker">MONETARY TRANSMISSION & REAL-ECONOMY STRESS</p>
            <h1>금융→실물경제 충격 전이 모니터</h1>
            <p className="topbar__desc">금리 수준보다 <strong>방향·변곡점·스프레드·전이 순서</strong>를 우선해서 봅니다.</p>
          </div>
          <div className="topbar__controls">
            <div className="range-control">
              <span>차트 기간</span>
              <div className="range-switch" aria-label="차트 표시기간 선택">
                {ranges.map((r) => <button key={r} className={range === r ? "is-active" : ""} onClick={() => setRange(r)}>{r}</button>)}
              </div>
            </div>
            <DiagnosticExportButton bundle={data} status={status} range={range} version={APP_VERSION} situationAnalysis={situationAnalysis} />
          </div>
        </header>

        <DataStatusStrip status={status} />

        {activeSection === "overview" && (
          <section id="overview" className="overview-tab">
            <SituationPanel items={situationItems} analysis={situationAnalysis} />

            <div className="subsection-heading overview-metrics-heading">
              <div><p className="kicker">근거 숫자</p><h3>위 설명을 만든 핵심 지표</h3></div>
              <p>숫자부터 외우지 않아도 됩니다. 각 카드의 한 줄 해석을 먼저 읽고, 필요할 때 ‘지표 뜻’을 펼쳐 정의를 확인합니다.</p>
            </div>
            <section className="metrics-grid metrics-grid--overview">
              <MetricCard label="BOK 기준금리" value={fmt(headline.bok)} unit="%" delta={latestChange(bok)} deltaUnit="%p" deltaDigits={2} eyebrow="POLICY" status={metricStatus(bok, headline.bok)} takeaway={metricTakeaway(bok, "policy")} help={metricHelp(bok, "policy")}/>
              <MetricCard label="국고채 3년" value={fmt(headline.ktb3)} unit="%" delta={latestChange(ktb3)} deltaUnit="%p" deltaDigits={2} eyebrow="MARKET" status={metricStatus(ktb3, headline.ktb3)} takeaway={metricTakeaway(ktb3, "market")} help={metricHelp(ktb3, "market")}/>
              <MetricCard label="정책 기대차" value={fmt(headline.gap, 1)} unit="bp" delta={latestChange(policyGap)} deltaUnit="bp" deltaDigits={1} eyebrow="EXPECTATION GAP" status={metricStatus(policyGap, headline.gap)} takeaway={metricTakeaway(policyGap, "policy_gap")} help={metricHelp(policyGap, "policy_gap")}/>
              <MetricCard label="10Y-3Y" value={fmt(headline.curve, 1)} unit="bp" delta={latestChange(curve103)} deltaUnit="bp" deltaDigits={1} eyebrow="CURVE" status={metricStatus(curve103, headline.curve)} takeaway={metricTakeaway(curve103, "curve")} help={metricHelp(curve103, "curve")}/>
              <MetricCard label="신규-잔액 조달" value={fmt(headline.funding, 1)} unit="bp" delta={latestChange(fundingMomentum)} deltaUnit="bp" deltaDigits={1} eyebrow="FUNDING PASS-THROUGH" status={metricStatus(fundingMomentum, headline.funding)} takeaway={metricTakeaway(fundingMomentum, "funding")} help={metricHelp(fundingMomentum, "funding")}/>
              <MetricCard label="신용 스프레드" value={fmt(headline.credit, 1)} unit="bp" delta={latestChange(creditSpread)} deltaUnit="bp" deltaDigits={1} eyebrow="CREDIT" status={metricStatus(creditSpread, headline.credit)} takeaway={metricTakeaway(creditSpread, "credit")} help={metricHelp(creditSpread, "credit")}/>
              <MetricCard label="기존 주담대 금리" value={fmt(headline.existingMortgage)} unit="%" delta={latestChange(legacyMortgage.inputs.existingMortgageCost)} deltaUnit="%p" deltaDigits={2} eyebrow="HOUSING STOCK" status={metricStatus(legacyMortgage.inputs.existingMortgageCost, headline.existingMortgage)} takeaway={metricTakeaway(legacyMortgage.inputs.existingMortgageCost, "mortgage_stock")} help="예금은행 잔액 기준 주택담보대출 금리. 신규취급금리와 분리해서 기존차주의 평균 금리부담을 봅니다."/>
              <MetricCard label="락인 스프레드" value={fmt(headline.lockIn, 2)} unit="%p" delta={latestChange(legacyMortgage.lockInSpread)} deltaUnit="%p" deltaDigits={2} eyebrow="HOUSING FLOW-STOCK" status={metricStatus(legacyMortgage.lockInSpread, headline.lockIn)} takeaway={metricTakeaway(legacyMortgage.lockInSpread, "lock_in")} help="신규 주담대금리 − 기존 주담대 잔액금리. 거래·매도 확률이 아니라 신규/기존 금융비용 격차입니다."/>
              <MetricCard label="주택가격전망 CSI" value={fmt(headline.housingOutlook, 0)} unit="" delta={latestChange(legacyMortgage.inputs.housingPriceOutlook)} deltaUnit="" deltaDigits={0} eyebrow="HOUSING EXPECTATION" status={metricStatus(legacyMortgage.inputs.housingPriceOutlook, headline.housingOutlook)} takeaway={metricTakeaway(legacyMortgage.inputs.housingPriceOutlook, "housing_outlook")} help="한국은행 소비자동향조사의 주택가격전망 CSI. 실제 가격지수나 가격상승률이 아닙니다."/>
            </section>

            <section className="architecture-card overview-architecture">
              <div>
                <p className="kicker">전이 구조</p>
                <h2>전체 전이는 개요에서 먼저 요약</h2>
                <p>각 탭에서는 원계열과 세부 경로를 깊게 보고, 개요에서는 금융→대출→주택/신용→실물의 최신 상태만 함께 봅니다. Stress 임계값이나 위기확률은 검증 전에는 생성하지 않습니다.</p>
              </div>
              <div className="flow">
                {["정책", "한미 시장금리", "환율", "은행 조달", "대출 전이", "주택", "신용", "실물경제"].map((x, i, arr) => (
                  <div className="flow__node" key={x}><span>{String(i + 1).padStart(2, "0")}</span><strong>{x}</strong>{i < arr.length - 1 && <b>→</b>}</div>
                ))}
              </div>
            </section>
          </section>
        )}

        {activeSection === "rates" && (
          <section id="rates" className="section-block tab-panel">
          <SectionHeader step="01" title="정책금리와 시장금리" description="ECOS 일별 공식계열. 정책금리는 해당 시점의 유효 금리를 유지(step-hold)해 시장금리와 비교합니다." />
          <TabNarrative narrative={tabNarratives.rates} />
          <div className="chart-grid chart-grid--wide">
            <TimeSeriesChart
              title="기준금리 vs 국고채"
              subtitle="정책금리와 3·5·10년 시장금리의 방향·변곡점"
              series={[bok, ktb3, ktb5, ktb10]}
              range={range}
              events={events}
              height={340}
              onOpenDetail={() => openDetail({
                title: "기준금리 vs 국고채",
                description: "정책금리와 시장금리의 선후관계를 Level·변화량·Z-score·percentile로 분리해 확인합니다.",
                series: [bok, ktb3, ktb5, ktb10]
              })}
            />
            <TimeSeriesChart
              title="정책 기대차"
              subtitle="국고채 3년 - 기준금리. 시장 기대가 정책금리보다 얼마나 앞서 있는지 확인합니다."
              series={[policyGap]}
              range={range}
              events={events}
              referenceZero
              height={260}
              onOpenDetail={() => openDetail({
                title: "정책 기대차",
                description: "국고채 3년 관측일에 당시 유효한 기준금리를 step-hold하여 계산합니다. 숫자 보간은 하지 않습니다.",
                series: [policyGap],
                referenceZero: true
              })}
            />
            <TimeSeriesChart
              title="Yield curve"
              subtitle="국고채 10년 - 3년. 정책 기대차와 별도 신호로 봅니다."
              series={[curve103]}
              range={range}
              events={events}
              referenceZero
              height={260}
              onOpenDetail={() => openDetail({
                title: "Yield curve 10Y-3Y",
                description: "같은 거래일의 국고채 10년·3년만 사용합니다.",
                series: [curve103],
                referenceZero: true
              })}
            />
          </div>
          </section>
        )}

        {activeSection === "global" && (
          <section id="global" className="section-block tab-panel">
          <SectionHeader
            step="02"
            title="한미 금리차와 환율 확인"
            description="미 재무부 공식 국채수익률, NY Fed의 FOMC 목표범위, ECOS USD/KRW를 분리 수집해 교차확인합니다."
          />
          <TabNarrative narrative={tabNarratives.global} />
          <div className="chart-grid">
            <TimeSeriesChart
              title="미국 국채 수익률곡선"
              subtitle="U.S. Treasury Daily Par Yield Curve · 2/3/5/10Y"
              series={[ust2, ust3, ust5, ust10]}
              range={range}
              onOpenDetail={() => openDetail({
                title: "미국 국채 수익률곡선",
                description: "미 재무부 연도별 공식 CSV를 Pages Function에서 수집합니다. 결측 tenor/휴장일은 보간하지 않습니다.",
                series: [ust2, ust3, ust5, ust10]
              })}
            />
            <TimeSeriesChart
              title="한국-미국 중단기 금리차"
              subtitle="KTB-UST 2Y/3Y · 양국 모두 관측된 같은 달력일만 계산"
              series={[krUs2Spread, krUs3Spread]}
              range={range}
              referenceZero
              onOpenDetail={() => openDetail({
                title: "한국-미국 중단기 금리차",
                description: "환율의 단독 결정변수가 아니라 상대적 기대금리 경로를 보는 확인계열입니다. 휴장일 forward-fill은 하지 않습니다.",
                series: [krUs2Spread, krUs3Spread],
                referenceZero: true
              })}
            />
            <TimeSeriesChart
              title="BOK vs Fed 목표범위"
              subtitle="Fed 상·하단과 비교용 중간값 · EFFR와 구분"
              series={[bok, fedLower, fedUpper, fedMid]}
              range={range}
              onOpenDetail={() => openDetail({
                title: "BOK vs Fed 목표범위",
                description: "Fed mid는 목표범위 상·하단의 단순 산술평균입니다. 실제 연방기금 유효금리(EFFR)가 아닙니다.",
                series: [bok, fedLower, fedUpper, fedMid]
              })}
            />
            <TimeSeriesChart
              title="정책금리차"
              subtitle="BOK - Fed target midpoint"
              series={[bokFedSpread]}
              range={range}
              referenceZero
              onOpenDetail={() => openDetail({
                title: "BOK-Fed 정책금리차",
                description: "비교 편의를 위한 목표금리 중간값 기준 산술 스프레드입니다.",
                series: [bokFedSpread],
                referenceZero: true
              })}
            />
            <TimeSeriesChart
              title="USD/KRW와 한미 2Y 금리차"
              subtitle="환율(좌축)과 금리차(우축)를 같은 시간축에서 확인"
              series={[usdKrw, krUs2Spread]}
              range={range}
              referenceZero={false}
              onOpenDetail={() => openDetail({
                title: "USD/KRW와 한미 2Y 금리차",
                description: "동행·선행 여부를 시각적으로 확인하되 금리차만으로 환율을 설명하거나 인과판정하지 않습니다.",
                series: [usdKrw, krUs2Spread]
              })}
            />
          </div>
          </section>
        )}

        {activeSection === "credit" && (
          <section id="credit" className="section-block tab-panel">
          <SectionHeader
            step="03"
            title="시장금리 → 은행조달 → 실제 대출금리"
            description="은행채 AAA 자체보다 대출금리 전이를 보는 목적에 맞춰 ECOS 공식계열만으로 본체를 구성합니다."
          />
          <TabNarrative narrative={tabNarratives.credit} />
          <div className="chart-grid">
            <TimeSeriesChart
              title="대출 선행 시장금리"
              subtitle="장기: 국고채 5Y · 단기: KORIBOR12M / CD91"
              series={[ktb5, koribor12, cd91]}
              range={range}
              events={events}
              onOpenDetail={() => openDetail({
                title: "대출 선행 시장금리",
                description: "고정형·변동형 대출에 가까운 시장금리 방향을 분리해서 봅니다.",
                series: [ktb5, koribor12, cd91]
              })}
            />
            <TimeSeriesChart
              title="예금은행 금융채 조달비용"
              subtitle="신규취급금리와 잔액금리: 새 조달비용과 누적 조달비용을 분리"
              series={[fundingNew, fundingStock]}
              range={range}
              onOpenDetail={() => openDetail({
                title: "예금은행 금융채 조달비용",
                description: "121Y002 금융채 신규취급금리와 121Y013 금융채 잔액금리를 직접 비교합니다.",
                series: [fundingNew, fundingStock]
              })}
            />
            <TimeSeriesChart
              title="신규-잔액 금융채 금리"
              subtitle="신규 조달환경이 기존 잔액비용보다 얼마나 빠르게 움직이는지 확인"
              series={[fundingMomentum]}
              range={range}
              referenceZero
              onOpenDetail={() => openDetail({
                title: "신규-잔액 금융채 금리",
                description: "양수 확대는 신규 금융채 조달비용이 기존 잔액비용보다 높은 상태입니다. 위기확률로 해석하지 않습니다.",
                series: [fundingMomentum],
                referenceZero: true
              })}
            />
            <TimeSeriesChart
              title="실제 신규 대출금리"
              subtitle="기업·가계·주담대. ITEM_CODE는 현재 ECOS 메타데이터로 재검증/해결"
              series={[corpLoan, householdLoan, mortgageLoan]}
              range={range}
              onOpenDetail={() => openDetail({
                title: "실제 신규 대출금리",
                description: "금리 충격이 실제 신규 차주에게 전달된 결과층입니다.",
                series: [corpLoan, householdLoan, mortgageLoan]
              })}
            />
            <TimeSeriesChart
              title="대출금리 - 신규 금융채 조달"
              subtitle="기업·주담대 pricing spread. 회계상 순마진으로 단정하지 않음"
              series={[corpLoanFundingSpread, mortgageFundingSpread]}
              range={range}
              referenceZero
              onOpenDetail={() => openDetail({
                title: "대출금리 - 신규 금융채 조달",
                description: "상품구성·신용위험·운영비·은행 경쟁이 함께 들어간 관찰 스프레드입니다.",
                series: [corpLoanFundingSpread, mortgageFundingSpread],
                referenceZero: true
              })}
            />
            <TimeSeriesChart
              title="기업 신용스프레드"
              subtitle="회사채 AA- 3년(민평 우선) - 국고채 3년"
              series={[corpAA, ktb3, creditSpread]}
              range={range}
              events={events}
              onOpenDetail={() => openDetail({
                title: "기업 신용스프레드",
                description: "민평계열이 정상 수집되면 민평을 우선하고, 실패 시 일반 AA- 계열로만 fallback합니다. fallback 여부는 source metadata에서 확인할 수 있습니다.",
                series: [corpAA, ktb3, creditSpread]
              })}
            />
          </div>

          <div className="subsection-heading">
            <div><p className="kicker">HISTORICAL LEAD / LAG</p><h3>시장금리 변화가 대출금리에 어느 구간에서 이어졌나</h3></div>
            <p>월간 1차 차분 기준. 금융→대출은 즉시(0~1M)·단기(2~3M)·지연(4~6M) 구간으로 나눠 보고, 한 달의 최대 r보다 같은 방향이 여러 시차에서 이어지는지를 먼저 봅니다.</p>
          </div>
          <LagAnalysisTable scans={data?.lagScans ?? []} />
          </section>
        )}

        {activeSection === "housing" && (
          <section id="housing" className="section-block tab-panel">
          <SectionHeader
            step="04"
            title="부동산 금리전이"
            description="금리→주담대→거래량→주택가격 경로를 MAX 역사자료에서 추적합니다. 공식 아파트 매매가격지수를 주력 가격계열로, RTMS 가격대별 실거래 proxy를 세분화 보조계열로 사용합니다."
          />
          <TabNarrative narrative={tabNarratives.housing} />
          <HousingTransmissionSection housing={housing} mortgageRate={mortgageLoan} fundingRate={fundingNew} benchmarkRate={ktb5} legacyMortgage={legacyMortgage} housingLagScans={data?.housingLagScans ?? []} housingRelationshipAnalysis={data?.housingRelationshipAnalysis ?? createBlockedHousingRelationshipAnalysis()} officialHousingPrices={data?.officialHousingPrices ?? []} historicalCoverage={data?.historicalCoverage ?? { generatedAt: new Date(0).toISOString(), items: [], maxReady: 0, total: 0 }} range={range} onOpenDetail={openDetail} />
          </section>
        )}

        {activeSection === "real" && (
          <section id="real" className="section-block tab-panel">
          <SectionHeader
            step="05"
            title="실물경제 확인층"
            description="ECOS의 심리·생산·주택 재고까지 자동 연결을 시도합니다. 다차원 메타데이터가 모호하면 첫 항목을 임의 선택하지 않고 N/A와 필요한 Group 후보를 표시합니다."
          />
          <TabNarrative narrative={tabNarratives.real} />
          <div className="chart-grid">
            <TimeSeriesChart
              title="경제심리지수"
              subtitle="513Y001 / E1000. 금융여건 변화가 기업·가계 심리에 반영되는 확인층"
              series={[economicSentiment]}
              range={range}
              onOpenDetail={() => openDetail({ title: "경제심리지수", description: economicSentiment.source.note ?? "경제심리지수 원계열", series: [economicSentiment] })}
            />
            <TimeSeriesChart
              title="전국 미분양주택"
              subtitle="901Y074. ECOS 전국 총 미분양을 사용하며 추가 차원이 모호하면 N/A로 중단"
              series={[unsoldTotal]}
              range={range}
              onOpenDetail={() => openDetail({ title: "전국 미분양주택", description: unsoldTotal.source.note ?? "", series: [unsoldTotal] })}
            />
            <TimeSeriesChart
              title="전산업생산지수"
              subtitle="901Y033. 금융충격이 실제 생산활동으로 전달됐는지 확인하는 후행층"
              series={[industrialProduction]}
              range={range}
              onOpenDetail={() => openDetail({ title: "전산업생산지수(농림어업제외)", description: industrialProduction.source.note ?? "", series: [industrialProduction] })}
            />
          </div>
          </section>
        )}

        {activeSection === "sources" && (
          <section id="sources" className="source-readiness tab-panel">
          <SectionHeader step="06" title="공식 데이터 연결·검증 상태" description="상위 원천의 수집방식·인증과 FREM의 실제 소비 경로를 분리해 표시합니다. 현재 production에서 중앙화된 원천은 SDP가 수집을 소유하고 FREM은 read-only consumer이며, 공식 동일정의 계열이 없어 의도적으로 N/A인 계산은 연결 실패로 보지 않습니다." />
          <TabNarrative narrative={tabNarratives.sources} />
          <div className="source-table-wrap">
            <table className="source-table">
              <thead><tr><th>상위 원천</th><th>대상</th><th>원천 수집</th><th>수집 소유</th><th>FREM 경로</th><th>원천 인증</th><th>FREM provider 자격증명</th><th>코드상태</th><th>다음 요구사항</th></tr></thead>
              <tbody>
                {sourceRegistry.map((source) => (
                  <tr key={source.id}>
                    <td><strong>{source.provider}</strong><br/><small>{source.dataset}</small></td>
                    <td>{source.targetSeries.join(", ")}</td>
                    <td>{source.acquisition}</td>
                    <td>{source.collectionOwner}</td>
                    <td>{source.fremTransport}</td>
                    <td>{source.auth}</td>
                    <td>{source.providerCredentialInFrem}</td>
                    <td><span className={`readiness readiness--${source.status}`} title={source.status}>{sourceStatusLabels[source.status]}</span></td>
                    <td>{source.userAction ?? "추가 사용자 결정 없음"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {status.issues.length > 0 && (
            <div className="issue-panel">
              <h3>수집/검증 이슈</h3>
              {status.issues.map((issue) => (
                <div key={issue.id} className={`issue-row issue-row--${issue.severity}`}>
                  <strong>{issue.scope}</strong><span>{issue.message}</span>
                </div>
              ))}
            </div>
          )}
          </section>
        )}

        <footer className="footer">
          <span>{APP_VERSION} · 금융→대출→주택/신용→실물 전이 모니터</span>
          <span>공식 데이터 누락 → N/A · 무음 대체 없음 · 원자료 계보 표시</span>
        </footer>
      </main>

      {detail && (
        <DetailPanel
          open={Boolean(detail)}
          title={detail.title}
          description={detail.description}
          series={detail.series}
          events={events}
          initialRange={range}
          referenceZero={detail.referenceZero}
          onClose={() => setDetail(null)}
        />
      )}
    </div>
  );
}
